import { useState, type FormEvent } from "react"
import type {
  PortalBrandingResponse,
  PortalSessionResponse,
  PortalSigninLinkFailure,
} from "@ledgerbox/domain"

import { errorMessage } from "./api"
import { usePortalApi } from "./api-context"
import {
  authNote,
  firstSigninStep,
  shownAddress,
  type SigninIdentity,
  type SigninStep,
} from "./sign-in-model"
import { InlineError } from "./ui"

/* One panel. Usually two steps: the address the firm has for the client,
   then the six digits. A link from the email that could not sign the client
   in starts one step further on, with the address already known: a fresh
   link when it expired, a code when it was already used. */
export function SignIn({
  branding,
  linkToken,
  notice,
  signinToken = null,
  linkFailure = null,
  onSignedIn,
  onBack,
}: {
  branding: PortalBrandingResponse
  linkToken: string
  notice?: string | null
  /** The token from the email, already out of the address bar. */
  signinToken?: string | null
  linkFailure?: PortalSigninLinkFailure | null
  onSignedIn: (session: PortalSessionResponse) => void
  /** Back to sending with only the link, when the link allows it. */
  onBack?: () => void
}) {
  const api = usePortalApi()
  const [step, setStep] = useState<SigninStep>(() =>
    firstSigninStep(linkFailure, signinToken)
  )
  const [email, setEmail] = useState("")
  const [code, setCode] = useState("")
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async (work: () => Promise<void>) => {
    setPending(true)
    setError(null)
    try {
      await work()
    } catch (failure) {
      setError(
        errorMessage(
          failure instanceof Error
            ? failure
            : new Error("Something went wrong. Try again.")
        )
      )
    } finally {
      setPending(false)
    }
  }

  const sendCode = (identity: SigninIdentity) =>
    run(async () => {
      const response =
        identity.kind === "email"
          ? await api.requestCode({ linkToken, email: identity.email })
          : await api.requestSigninLinkCode({
              linkToken,
              signinToken: identity.signinToken,
            })
      setStep({ kind: "code", challengeId: response.challengeId, identity })
      setCode("")
    })

  const verify = (event: FormEvent) => {
    event.preventDefault()
    if (step.kind !== "code") return
    const { challengeId, identity } = step
    void run(async () =>
      onSignedIn(
        identity.kind === "email"
          ? await api.verifyCode({
              linkToken,
              challengeId,
              email: identity.email,
              code,
            })
          : await api.verifySigninLinkCode({
              linkToken,
              signinToken: identity.signinToken,
              challengeId,
              code,
            })
      )
    )
  }

  const useDifferentAddress = () => {
    setError(null)
    setStep({ kind: "email", notice: null })
  }

  const differentAddress = (
    <div className="auth-actions">
      <button
        type="button"
        className="text-button"
        onClick={useDifferentAddress}
        disabled={pending}
      >
        Use a different address
      </button>
    </div>
  )

  const shownNotice = notice ?? (step.kind === "email" ? step.notice : null)

  // The notice reads under the page's title, not above it (R1-U4).
  const noticeLine = shownNotice ? (
    <p role="status" className="portal-switch-notice">
      {shownNotice}
    </p>
  ) : null

  return (
    <section className="auth-panel" aria-labelledby="sign-in-title">
      {step.kind === "code" ? (
        <>
          <h1 id="sign-in-title">Enter your code</h1>
          {noticeLine}
          <p className="auth-copy">
            We emailed six digits to <b>{shownAddress(step.identity)}</b>. It
            works for ten minutes.
          </p>
          <form onSubmit={verify} className="auth-form">
            <label htmlFor="code" className="visually-hidden">
              Six-digit code
            </label>
            <input
              id="code"
              className="code-input"
              value={code}
              onChange={(event) =>
                setCode(
                  event.currentTarget.value.replace(/\D/g, "").slice(0, 6)
                )
              }
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              pattern="[0-9]{6}"
              placeholder="000000"
              required
            />
            {error ? <InlineError message={error} /> : null}
            <button
              className="accent-pill"
              disabled={pending || code.length !== 6}
            >
              {pending ? "Checking…" : "Open my documents"}
            </button>
            <div className="auth-actions">
              <button
                type="button"
                className="text-button"
                onClick={() => void sendCode(step.identity)}
                disabled={pending}
              >
                Email a new code
              </button>
              <button
                type="button"
                className="text-button"
                onClick={useDifferentAddress}
                disabled={pending}
              >
                Use a different address
              </button>
            </div>
          </form>
        </>
      ) : step.kind === "reissued" ? (
        <>
          <h1 id="sign-in-title">Check your email</h1>
          {noticeLine}
          {/* The API answers the same way when it sent nothing (this link
              was already replaced, or the address is not on file), so the
              copy says what is true in every case. */}
          <p className="auth-copy" role="status">
            If we can replace this link, a new one is on its way to{" "}
            <b>{step.maskedEmail}</b>. You can use it one time in the next 72
            hours.
          </p>
          <p className="auth-copy">
            You get one new link for each expired link. If you asked before,
            use the link in that email, or sign in with a code.
          </p>
          <div className="auth-actions">
            <button
              type="button"
              className="text-button"
              onClick={useDifferentAddress}
              disabled={pending}
            >
              Sign in with a code
            </button>
          </div>
        </>
      ) : step.kind === "expired" || step.kind === "used" ? (
        <>
          <h1 id="sign-in-title">Your documents for {branding.displayName}</h1>
          {noticeLine}
          <p className="auth-copy">
            {step.kind === "expired"
              ? "Your link expired. We can email a new link to "
              : "This link was already used. We can email a six-digit code to "}
            <b>{step.maskedEmail}</b>.
          </p>
          <form
            className="auth-form"
            onSubmit={(event) => {
              event.preventDefault()
              const { signinToken, maskedEmail } = step
              if (step.kind === "used") {
                void sendCode({ kind: "link", signinToken, maskedEmail })
                return
              }
              void run(async () => {
                await api.reissueSigninLink({ linkToken, signinToken })
                setStep({ kind: "reissued", maskedEmail })
              })
            }}
          >
            {error ? <InlineError message={error} /> : null}
            <button className="accent-pill" disabled={pending}>
              {pending
                ? "Emailing…"
                : step.kind === "expired"
                  ? "Email me a new link"
                  : "Email me a code"}
            </button>
            {differentAddress}
          </form>
        </>
      ) : (
        <>
          <h1 id="sign-in-title">Your documents for {branding.displayName}</h1>
          {noticeLine}
          <p className="auth-copy">
            Enter the email address {branding.displayName} has for you. We send
            a six-digit code to it.
          </p>
          <form
            onSubmit={(event) => {
              event.preventDefault()
              void sendCode({ kind: "email", email })
            }}
            className="auth-form"
          >
            <label htmlFor="email">Email address</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
              placeholder="you@company.com"
              autoComplete="email"
              autoFocus
              required
            />
            {error ? <InlineError message={error} /> : null}
            <button className="accent-pill" disabled={pending}>
              {pending ? "Emailing…" : "Email me a code"}
            </button>
          </form>
        </>
      )}
      {onBack ? (
        <div className="auth-actions">
          <button type="button" className="text-button" onClick={onBack}>
            Send files without signing in
          </button>
        </div>
      ) : null}
      <div className="auth-notes">
        <p>{authNote(step)}</p>
      </div>
    </section>
  )
}
